import { useState } from 'react'
import {
  Box,
  Button,
  Popover,
  Paper,
  Typography,
  Chip,
  Stack,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { CalendarToday } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'

interface DateRangePickerProps {
  startDate?: Date | null
  endDate?: Date | null
  onChange: (startDate: Date | null, endDate: Date | null) => void
  label?: string
}

const DateRangePicker = ({ startDate, endDate, onChange, label = 'Date Range' }: DateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(startDate ? dayjs(startDate) : null)
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(endDate ? dayjs(endDate) : null)

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setTempStartDate(startDate ? dayjs(startDate) : null)
    setTempEndDate(endDate ? dayjs(endDate) : null)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleApply = () => {
    onChange(
      tempStartDate ? tempStartDate.toDate() : null,
      tempEndDate ? tempEndDate.toDate() : null
    )
    handleClose()
  }

  const handleClear = () => {
    setTempStartDate(null)
    setTempEndDate(null)
    onChange(null, null)
    handleClose()
  }

  const handleQuickSelect = (start: Dayjs, end: Dayjs) => {
    setTempStartDate(start)
    setTempEndDate(end)
  }

  const formatDateRange = () => {
    if (!startDate && !endDate) return 'Select date range'
    if (!startDate) return `Until ${dayjs(endDate).format('MMM DD, YYYY')}`
    if (!endDate) return `From ${dayjs(startDate).format('MMM DD, YYYY')}`
    return `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format('MMM DD, YYYY')}`
  }

  const quickRanges = [
    { label: 'Last 7 days', start: dayjs().subtract(7, 'day'), end: dayjs() },
    { label: 'Last 30 days', start: dayjs().subtract(30, 'day'), end: dayjs() },
    { label: 'This month', start: dayjs().startOf('month'), end: dayjs().endOf('month') },
    { label: 'Last month', start: dayjs().subtract(1, 'month').startOf('month'), end: dayjs().subtract(1, 'month').endOf('month') },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Button
          variant="outlined"
          onClick={handleClick}
          startIcon={<CalendarToday />}
          sx={{ justifyContent: 'flex-start', minWidth: 200 }}
        >
          {formatDateRange()}
        </Button>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Paper sx={{ p: 3, minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              {label}
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Typography variant="subtitle2">Quick Select</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {quickRanges.map((range) => (
                  <Chip
                    key={range.label}
                    label={range.label}
                    onClick={() => handleQuickSelect(range.start, range.end)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Stack>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <DatePicker
                label="Start Date"
                value={tempStartDate}
                onChange={setTempStartDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End Date"
                value={tempEndDate}
                onChange={setTempEndDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={handleClear} color="inherit">
                Clear
              </Button>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button onClick={handleApply} variant="contained">
                Apply
              </Button>
            </Stack>
          </Paper>
        </Popover>
      </Box>
    </LocalizationProvider>
  )
}

export default DateRangePicker